import React, { useState, useEffect } from 'react';
import { pageSectionService } from '../../services/api';

const SECTION_TYPES = [
  { value: 'image_text', label: 'Image & Text' },
  { value: 'pillars', label: 'Pillars / Key Features' },
  { value: 'portfolio', label: 'Portfolio Brands' },
  { value: 'local_spotlight', label: 'Local Spotlight' },
  { value: 'cta', label: 'Call to Action' },
  { value: 'content', label: 'Simple Content' },
  { value: 'feature_grid', label: 'Feature Grid' },
  { value: 'dynamic_showcase', label: 'Dynamic Showcase (Trending)' },
];

const BG_COLOR_PRESETS = [
  { value: 'white', label: 'White', class: 'bg-white' },
  { value: 'slate-50', label: 'Light Gray', class: 'bg-slate-50' },
  { value: 'slate-900', label: 'Dark', class: 'bg-slate-900' },
  { value: 'red-50', label: 'Light Red', class: 'bg-red-50' },
  { value: 'blue-50', label: 'Light Blue', class: 'bg-blue-50' },
  { value: 'orange-50', label: 'Light Orange', class: 'bg-orange-50' },
  { value: 'purple-50', label: 'Light Purple', class: 'bg-purple-50' },
  { value: 'teal-50', label: 'Light Teal', class: 'bg-teal-50' },
];

const GRADIENT_PRESETS = [
  'from-orange-500 via-red-500 to-pink-500',
  'from-blue-500 via-indigo-500 to-purple-500',
  'from-teal-500 via-cyan-500 to-blue-500',
  'from-purple-500 via-pink-500 to-rose-500',
  'from-amber-500 via-orange-500 to-red-500',
  'from-green-500 via-emerald-500 to-teal-500',
];

const emptyForm = {
  page: 'home', section_type: 'dynamic_showcase', title: '', subtitle: '', content: '',
  image: '', button_text: '', button_link: '', bg_color: 'white', text_color: 'dark',
  sort_order: 0, is_active: true, meta: { items: [] },
};

function PageSectionsManagement() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { loadSections(); }, []);

  const loadSections = async () => {
    setLoading(true);
    try { setSections(await pageSectionService.index()); } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (form.section_type !== 'dynamic_showcase' && (!payload.meta || (payload.meta.items && payload.meta.items.length === 0))) {
      delete payload.meta;
    }
    try {
      if (editingId) await pageSectionService.update(editingId, payload);
      else await pageSectionService.store(payload);
      setShowForm(false); setEditingId(null); setForm(emptyForm);
      loadSections();
    } catch (error) { alert('Error saving section: ' + (error.response?.data?.message || error.message)); }
  };

  const handleEdit = (s) => {
    const formData = { ...s };
    if (!formData.meta) formData.meta = { items: [] };
    if (!formData.meta.items) formData.meta.items = [];
    setForm(formData); setEditingId(s.id); setShowForm(true);
  };
  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; try { await pageSectionService.destroy(id); loadSections(); } catch (e) { alert('Error'); } };

  const updateMetaItem = (idx, field, value) => {
    const items = [...(form.meta?.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, meta: { ...form.meta, items } });
  };

  const addMetaItem = () => {
    const items = [...(form.meta?.items || []), { title: '', desc: '', image: '', link: '', badge: '', icon: '', gradient: '' }];
    setForm({ ...form, meta: { ...form.meta, items } });
  };

  const removeMetaItem = (idx) => {
    const items = (form.meta?.items || []).filter((_, i) => i !== idx);
    setForm({ ...form, meta: { ...form.meta, items } });
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Page Sections</h2>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-semibold">+ Add Section</button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 mb-6 space-y-5 max-w-3xl">
          <h3 className="font-bold text-lg border-b pb-3">{editingId ? 'Edit' : 'New'} Section</h3>

          {/* Page & Section Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Page</label>
              <input required placeholder="home, about, etc." value={form.page} onChange={e => setForm({ ...form, page: e.target.value })} className="w-full border rounded-lg p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Section Type</label>
              <select value={form.section_type} onChange={e => setForm({ ...form, section_type: e.target.value })} className="w-full border rounded-lg p-2.5 bg-white">
                {SECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Title & Subtitle */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
              <input placeholder="Section title" value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full border rounded-lg p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Subtitle</label>
              <input placeholder="Section subtitle" value={form.subtitle || ''} onChange={e => setForm({ ...form, subtitle: e.target.value })} className="w-full border rounded-lg p-2.5" />
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Content (HTML allowed)</label>
            <textarea placeholder="Content body" value={form.content || ''} onChange={e => setForm({ ...form, content: e.target.value })} className="w-full border rounded-lg p-2.5" rows="3" />
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Image URL</label>
            <input placeholder="https://..." value={form.image || ''} onChange={e => setForm({ ...form, image: e.target.value })} className="w-full border rounded-lg p-2.5" />
          </div>

          {/* Button */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Button Text</label>
              <input placeholder="Learn More" value={form.button_text || ''} onChange={e => setForm({ ...form, button_text: e.target.value })} className="w-full border rounded-lg p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Button Link</label>
              <input placeholder="/about" value={form.button_link || ''} onChange={e => setForm({ ...form, button_link: e.target.value })} className="w-full border rounded-lg p-2.5" />
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Background Color</label>
              <select value={form.bg_color || 'white'} onChange={e => setForm({ ...form, bg_color: e.target.value })} className="w-full border rounded-lg p-2.5 bg-white">
                {BG_COLOR_PRESETS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Text Color</label>
              <select value={form.text_color || 'dark'} onChange={e => setForm({ ...form, text_color: e.target.value })} className="w-full border rounded-lg p-2.5 bg-white">
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
            </div>
          </div>

          {/* Sort Order & Active */}
          <div className="flex items-center gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Sort Order</label>
              <input type="number" placeholder="0" value={form.sort_order || 0} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="w-32 border rounded-lg p-2.5" />
            </div>
            <label className="flex items-center gap-2 mt-6 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="w-5 h-5" />
              <span className="font-semibold text-gray-700">Active</span>
            </label>
          </div>

          {/* Dynamic Showcase Items Editor */}
          {form.section_type === 'dynamic_showcase' && (
            <div className="border-t pt-5 mt-2">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-bold text-gray-800">Showcase Cards</h4>
                <button type="button" onClick={addMetaItem} className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700 transition font-semibold">+ Add Card</button>
              </div>
              {(form.meta?.items || []).length === 0 && (
                <p className="text-gray-400 text-sm italic py-4 text-center">No cards added yet. Click "Add Card" to create one.</p>
              )}
              <div className="space-y-4">
                {(form.meta?.items || []).map((item, idx) => (
                  <div key={idx} className="border rounded-xl p-4 bg-gray-50 space-y-3 relative">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-sm text-gray-600">Card #{idx + 1}</span>
                      <button type="button" onClick={() => removeMetaItem(idx)} className="text-red-500 hover:text-red-700 text-sm font-bold">✕ Remove</button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input placeholder="Title" value={item.title || ''} onChange={e => updateMetaItem(idx, 'title', e.target.value)} className="border rounded-lg p-2 text-sm" />
                      <input placeholder="Badge (e.g. New, Hot)" value={item.badge || ''} onChange={e => updateMetaItem(idx, 'badge', e.target.value)} className="border rounded-lg p-2 text-sm" />
                    </div>
                    <textarea placeholder="Description" value={item.desc || ''} onChange={e => updateMetaItem(idx, 'desc', e.target.value)} className="w-full border rounded-lg p-2 text-sm" rows="2" />
                    <input placeholder="Image URL" value={item.image || ''} onChange={e => updateMetaItem(idx, 'image', e.target.value)} className="w-full border rounded-lg p-2 text-sm" />
                    <div className="grid grid-cols-2 gap-3">
                      <input placeholder="Link (e.g. /shop)" value={item.link || ''} onChange={e => updateMetaItem(idx, 'link', e.target.value)} className="border rounded-lg p-2 text-sm" />
                      <input placeholder="Icon (emoji e.g. 🍽)" value={item.icon || ''} onChange={e => updateMetaItem(idx, 'icon', e.target.value)} className="border rounded-lg p-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Gradient Color</label>
                      <select value={item.gradient || ''} onChange={e => updateMetaItem(idx, 'gradient', e.target.value)} className="w-full border rounded-lg p-2 text-sm bg-white">
                        <option value="">Auto (rotate)</option>
                        {GRADIENT_PRESETS.map(g => <option key={g} value={g}>{g.replace(/from-|via-|to-/g, ' ').trim()}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-3 border-t">
            <button type="submit" className="bg-red-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-red-700 transition">{editingId ? 'Update' : 'Save'}</button>
            <button type="button" onClick={() => setShowForm(false)} className="bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-300 transition">Cancel</button>
          </div>
        </form>
      )}

      {/* Sections Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Page</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Order</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sections.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium">{s.page}</td>
                <td className="px-6 py-4 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${s.section_type === 'dynamic_showcase' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{s.section_type}</span>
                </td>
                <td className="px-6 py-4 text-sm">{s.title || '-'}</td>
                <td className="px-6 py-4 text-sm">{s.sort_order}</td>
                <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-6 py-4 flex gap-3">
                  <button onClick={() => handleEdit(s)} className="text-blue-600 text-sm font-semibold hover:text-blue-800">Edit</button>
                  <button onClick={() => handleDelete(s.id)} className="text-red-600 text-sm font-semibold hover:text-red-800">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sections.length === 0 && <div className="text-center py-12 text-gray-400">No sections found. Click "Add Section" to create one.</div>}
      </div>
    </div>
  );
}

export default PageSectionsManagement;
